import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Alert, Button, Card, Modal } from 'react-bootstrap';
import Form from 'react-bootstrap/Form';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Drawer } from 'rsuite';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSyncAlt } from '@fortawesome/free-solid-svg-icons';

import { Loading, PageHeader, Pagination, DrawerSeparator } from '../../components';
import DataTable from '../../components/DataTable/DataTable';
import Search from '../../components/Search/Search.component';
import useComponentState from '../../utils/useComponentState';
import { formatDateTime } from '../../utils/dataFormatter';
import { DATE_TIME_TYPES } from '../../constants/dataTimeTypes';
import {
  getCampaignCodes,
  searchCampaignCodes,
  uploadCampaignCodesSheet,
  downloadCampaignCodesCSV,
} from '../../api/campaignCodes';
import { getUploadErrorMessage } from '../UploadCampaignCodes/UploadCampaignCodesConstants';
import { ROLE_CLIENT_CAMPAIGN_ADMINISTRATOR } from '../../constants/roles';
import {
  INITIAL_STATE,
  STATUS_OPTIONS,
  TABLE_HEADING,
  NOT_FOUND_MESSAGE,
  DEFAULT_PAGE_SIZE,
  getCampaignCodesErrorMessage,
  hasActiveFilters,
} from './ViewCampaignCodesConstants';

import styles from './ViewCampaignCodes.module.scss';

const ViewCampaignCodes = ({ auth }) => {
  const [state, setState] = useComponentState(INITIAL_STATE);
  // Uploading allocation sheets is a Thyaga-admin-only backend operation
  // (see the acceptAsianPaintsReward comment in routes/index.js), so client
  // campaign administrators only get the read-only view of this page.
  const canUpload = auth?.details?.role !== ROLE_CLIENT_CAMPAIGN_ADMINISTRATOR;
  const {
    campaignCodes,
    currentPage,
    totalPages,
    loading,
    fetchError,
    filters,
    uploadedFile,
    uploadLoading,
    uploadError,
    uploadResult,
    showDownloadModal,
    downloadFilters,
    downloadingCSV,
  } = state;

  // Uses the plain list endpoint when no filters are set, otherwise the
  // combinable search endpoint - `filters` always reflects what's on screen.
  const runFetch = async (page, filtersToUse = filters) => {
    setState({ loading: true, fetchError: '' });
    try {
      const response = hasActiveFilters(filtersToUse)
        ? await searchCampaignCodes(
          auth.token,
          filtersToUse,
          page,
          DEFAULT_PAGE_SIZE
        )
        : await getCampaignCodes(auth.token, page, DEFAULT_PAGE_SIZE);

      setState({
        campaignCodes: response.campaignCodes || [],
        totalPages: response.totalPages || 0,
        currentPage: response.currentPage || 0,
      });
    } catch (err) {
      setState({
        campaignCodes: [],
        totalPages: 0,
        currentPage: 0,
        fetchError: getCampaignCodesErrorMessage(err?.message),
      });
    }
    setState({ loading: false });
  };

  useEffect(() => {
    runFetch(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileChange = (event) => {
    setState({
      uploadedFile: event.target.files[0] || null,
      uploadError: null,
      uploadResult: null,
    });
  };

  const handleUpload = async () => {
    if (!uploadedFile) {
      setState({ uploadError: 'Please select an .xlsx file to upload' });
      return;
    }

    setState({ uploadLoading: true, uploadError: null, uploadResult: null });

    try {
      const response = await uploadCampaignCodesSheet(uploadedFile, auth.token);
      setState({
        uploadLoading: false,
        uploadResult: response,
        uploadedFile: null,
      });

      if (response.insertedCount > 0) {
        toast.success(
          `${response.insertedCount} campaign code(s) uploaded successfully`
        );
      } else {
        toast.warning(
          'Nothing was inserted - every secret code in this sheet already exists'
        );
      }

      runFetch(0);
    } catch (err) {
      const message = getUploadErrorMessage(err?.message);
      setState({ uploadLoading: false, uploadError: message, uploadResult: null });
      toast.error(message);
    }
  };

  const handleCustomerSearch = (value) => {
    const updatedFilters = { ...filters, customer: value };
    setState({ filters: updatedFilters });
    runFetch(0, updatedFilters);
  };

  const handleCustomerClear = () => {
    const updatedFilters = { ...filters, customer: '' };
    setState({ filters: updatedFilters });
    runFetch(0, updatedFilters);
  };

  const handleCustomerNameSearch = (value) => {
    const updatedFilters = { ...filters, customerName: value };
    setState({ filters: updatedFilters });
    runFetch(0, updatedFilters);
  };

  const handleCustomerNameClear = () => {
    const updatedFilters = { ...filters, customerName: '' };
    setState({ filters: updatedFilters });
    runFetch(0, updatedFilters);
  };

  const handleSecretCodeSearch = (value) => {
    const updatedFilters = { ...filters, secretCode: value };
    setState({ filters: updatedFilters });
    runFetch(0, updatedFilters);
  };

  const handleSecretCodeClear = () => {
    const updatedFilters = { ...filters, secretCode: '' };
    setState({ filters: updatedFilters });
    runFetch(0, updatedFilters);
  };

  const handleAvailabilityChange = (event) => {
    const updatedFilters = { ...filters, status: event.target.value };
    setState({ filters: updatedFilters });
    runFetch(0, updatedFilters);
  };

  const handlePagination = (page) => {
    runFetch(page, filters);
  };

  const handleRefresh = () => {
    runFetch(currentPage, filters);
  };

  const handleOpenDownloadModal = () => {
    setState({
      showDownloadModal: true,
      downloadFilters: { status: '', startDate: null, endDate: null },
    });
  };

  const handleCloseDownloadModal = () => {
    if (downloadingCSV) return;
    setState({ showDownloadModal: false });
  };

  const handleDownloadFilterChange = (key, value) => {
    setState((prev) => ({
      downloadFilters: { ...prev.downloadFilters, [key]: value },
    }));
  };

  const handleDownloadCSV = async () => {
    setState({ downloadingCSV: true });
    try {
      const payload = {
        ids: null,
        status: downloadFilters.status || null,
        startDate: downloadFilters.startDate
          ? formatDateTime(downloadFilters.startDate, DATE_TIME_TYPES.ISO_DATE)
          : null,
        endDate: downloadFilters.endDate
          ? formatDateTime(downloadFilters.endDate, DATE_TIME_TYPES.ISO_DATE)
          : null,
      };
      const response = await downloadCampaignCodesCSV(payload, auth.token);
      const blob = new Blob([response], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'campaign-codes.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setState({ showDownloadModal: false });
    } catch (err) {
      toast.error(err?.message || 'Failed to download CSV');
    }
    setState({ downloadingCSV: false });
  };

  const existingSecretCodes = uploadResult?.existingSecretCodes || [];

  const renderUploadResult = () => {
    if (!uploadResult) {
      return null;
    }

    const { insertedCount } = uploadResult;

    return (
      <Alert
        variant={insertedCount > 0 ? 'success' : 'warning'}
        className={styles.upload_result_alert}
      >
        <strong>{insertedCount}</strong> campaign code(s) inserted.
      </Alert>
    );
  };

  const handleOnClick = (id) => {
    const rowData = campaignCodes.find((data) => data.id === id);
    if (!rowData) return;
    setState({ showDrawer: true, drawerData: rowData });
  };

  const handleDrawerOnHide = () => {
    setState({ showDrawer: false, drawerData: null });
  };

  const renderDrawerField = (label, value) => (
    <div className={styles.credential_row} key={label}>
      <span className={styles.credential_label}>{label}</span>
      <span className={styles.credential_value}>
        {value === null || value === undefined || value === '' ? '-' : String(value)}
      </span>
    </div>
  );

  const renderDrawer = () => {
    const { drawerData } = state;
    if (!drawerData) return null;

    return (
      <div className={styles.drawer_root}>
        <DrawerSeparator text='Campaign Code Details' />
        <div className={styles.credentials_box}>
          {renderDrawerField('Secret Code', drawerData.secretCode)}
          {renderDrawerField('Status', drawerData.status)}
          {renderDrawerField(
            'Inbound SMS Mobile Number',
            drawerData.inboundSmsCustomerMobileNumber
          )}
          {renderDrawerField('Inbound SMS ID', drawerData.inboundSmsId)}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.root_div}>
      <ToastContainer />
      <PageHeader text='Campaign Codes' />

      {canUpload && (
        <>
          <Card className={styles.upload_card}>
            <Card.Body>
              <div className={styles.upload_header_row}>
                <div className={styles.upload_info}>
                  <Card.Title className={styles.card_title}>
                    Upload allocation sheet
                  </Card.Title>
                  <Card.Text className={styles.card_subtext}>
                    Upload the .xlsx file
                  </Card.Text>
                </div>

                <div className={styles.upload_controls}>
                  <Form.Control
                    type='file'
                    accept='.xlsx'
                    onChange={handleFileChange}
                    disabled={uploadLoading}
                  />
                  <Button
                    variant='primary'
                    onClick={handleUpload}
                    disabled={uploadLoading || !uploadedFile}
                  >
                    {uploadLoading ? 'Uploading...' : 'Upload'}
                  </Button>
                </div>
              </div>

              {existingSecretCodes.length > 0 && (
                <div className={styles.existing_codes_block}>
                  Already exist codes: {existingSecretCodes.join(',')}.
                  <br></br>Remove them from the sheet and upload again.
                </div>
              )}

              {uploadedFile && !uploadError && (
                <p className={styles.selected_file}>
                  Selected: {uploadedFile.name}
                </p>
              )}

              {uploadError && (
                <Alert variant='danger' className={styles.error_alert}>
                  {uploadError}
                </Alert>
              )}
            </Card.Body>
          </Card>

          {uploadLoading && <Loading text='Uploading' />}

          {renderUploadResult()}
        </>
      )}

      <div className={styles.search_area_block}>
        <div className={styles.search_area}>
          <Search
            placeholder='Store'
            onBtnPress={handleCustomerSearch}
            onClearPress={handleCustomerClear}
            max={50}
          />
        </div>
        <div className={styles.search_area}>
          <Search
            placeholder='Store Name'
            onBtnPress={handleCustomerNameSearch}
            onClearPress={handleCustomerNameClear}
            max={50}
          />
        </div>
        <div className={styles.search_area}>
          <Search
            placeholder='Secret Code'
            onBtnPress={handleSecretCodeSearch}
            onClearPress={handleSecretCodeClear}
            max={50}
          />
        </div>
        <div className={styles.availability_filter}>
          <Form.Control
            as='select'
            className='form-select'
            value={filters.status}
            onChange={handleAvailabilityChange}
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Control>
        </div>
        <div className={styles.refresh_wrapper}>
          <Button
            variant='outline-secondary'
            onClick={handleRefresh}
            disabled={loading}
            title='Refresh'
          >
            <FontAwesomeIcon icon={faSyncAlt} spin={loading} />
          </Button>
        </div>
        <Button
          variant='dark'
          size='sm'
          className={styles.download_btn}
          onClick={handleOpenDownloadModal}
        >
          Download CSV
        </Button>
      </div>

      <div className='table-fit'>
        <DataTable
          tableData={campaignCodes}
          tableheading={TABLE_HEADING}
          tag='campaign-codes'
          fetchError={fetchError}
          notFoundMessage={NOT_FOUND_MESSAGE}
          search_filter_loading={loading}
          handleOnClick={handleOnClick}
        />
      </div>

      {!loading && !fetchError && (
        <div className={styles.pagination_wrapper}>
          <Pagination
            pageCount={totalPages}
            onChange={handlePagination}
            currentPage={currentPage}
          />
        </div>
      )}

      <Drawer show={state.showDrawer} onHide={handleDrawerOnHide}>
        {renderDrawer()}
      </Drawer>

      <Modal
        show={showDownloadModal}
        onHide={handleCloseDownloadModal}
        centered
        animation={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Download CSV</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className={styles.download_field}>
            <Form.Label>Status</Form.Label>
            <Form.Control
              as='select'
              className='form-select'
              value={downloadFilters.status}
              onChange={(e) => handleDownloadFilterChange('status', e.target.value)}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Control>
          </Form.Group>

          <Form.Group className={styles.download_field}>
            <Form.Label>Start Date</Form.Label>
            <DatePicker
              className='form-control'
              selected={downloadFilters.startDate}
              onChange={(date) => handleDownloadFilterChange('startDate', date)}
              selectsStart
              startDate={downloadFilters.startDate}
              endDate={downloadFilters.endDate}
              maxDate={downloadFilters.endDate || new Date()}
              dateFormat='yyyy-MM-dd'
              placeholderText='Select a start date'
              isClearable
            />
          </Form.Group>

          <Form.Group className={styles.download_field}>
            <Form.Label>End Date</Form.Label>
            <DatePicker
              className='form-control'
              selected={downloadFilters.endDate}
              onChange={(date) => handleDownloadFilterChange('endDate', date)}
              selectsEnd
              startDate={downloadFilters.startDate}
              endDate={downloadFilters.endDate}
              minDate={downloadFilters.startDate}
              maxDate={new Date()}
              dateFormat='yyyy-MM-dd'
              placeholderText='Select an end date'
              isClearable
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='secondary'
            onClick={handleCloseDownloadModal}
            disabled={downloadingCSV}
          >
            Cancel
          </Button>
          <Button variant='dark' onClick={handleDownloadCSV} disabled={downloadingCSV}>
            {downloadingCSV ? 'Downloading...' : 'Download'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    auth: state.auth.auth,
  };
};

export default connect(mapStateToProps)(ViewCampaignCodes);
